/**
 * Audit Log Table Component
 * 
 * Displays audit logs in a table format with:
 * - Timestamp
 * - Promo name
 * - Action badges
 * - Changed By
 * - Field
 * - Old Value
 * - New Value
 * - Pagination
 */

import React from 'react';
import { Table } from 'react-bootstrap';
import { AuditLog, AuditLogAction } from '../../../../api/promos/types';
import { AuditLogActionBadge } from '../../../../Common/BadgeStyles';
import '../AuditLogs.css';

interface AuditLogTableProps {
  auditLogs: AuditLog[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}


// Format timestamp for display
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${dateStr}, ${timeStr}`;
  } catch {
    return timestamp;
  }
};

// Format value for display
const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  // Handle objects (like user objects with {_id, email, name})
  if (typeof value === 'object' && !Array.isArray(value)) {
    // If it's an object, try to extract a meaningful string
    if (value.name) return value.name;
    if (value.email) return value.email;
    if (value.label) return value.label;
    if (value.value) return String(value.value);
    // If it's a date object
    if (value instanceof Date) {
      return value.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    // Fallback: stringify the object (for debugging)
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  if (typeof value === 'string') {
    // Check if it's a date string
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      try {
        const date = new Date(value);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      } catch {
        return value;
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(v => formatValue(v)).join(', ');
  }
  return String(value);
};

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  auditLogs,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="audit-log-table-wrapper">
      <Table responsive className="audit-log-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Promo</th>
            <th>Action</th>
            <th>Changed By</th>
            <th>Field</th>
            <th>Old Value</th>
            <th>New Value</th>
          </tr>
        </thead>
        <tbody>
          {auditLogs.map((log) => {
            return (
              <tr key={log._id}>
                {/* Timestamp */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {formatTimestamp(log.timestamp)}
                  </span>
                </td>
                
                {/* Promo */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {log.promoName || '-'}
                  </span>
                </td>
                
                {/* Action */}
                <td>
                  <AuditLogActionBadge 
                    action={log.action as 'CREATED' | 'UPDATED' | 'PAUSED' | 'ACTIVATED' | 'EXPIRED' | 'DELETED' | 'INACTIVATED'} 
                  />
                </td>
                
                {/* Changed By */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {log.changedBy || 'System'}
                  </span>
                </td>
                
                {/* Field */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {log.changes?.label || log.changes?.field || '-'}
                  </span>
                </td>
                
                {/* Old Value */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {formatValue(log.changes?.oldValue)}
                  </span>
                </td>
                
                {/* New Value */}
                <td>
                  <span style={{ fontSize: '13px', color: '#495057' }}>
                    {formatValue(log.changes?.newValue)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default AuditLogTable;
